/**
 * ProcessMonitor — server monitor for one connection (admin only). Rendered as
 * an inner tab from Browser.jsx.
 *
 * Two views, toggled in the header:
 *   • Processes    — information_schema.PROCESSLIST (sort/filter/multi-select).
 *   • Transactions — open InnoDB transactions (information_schema.INNODB_TRX
 *                    joined to PROCESSLIST): age, rows locked/modified, and
 *                    idle-in-transaction vs active — the lock-holder hunting view.
 *
 * Kill acts on the selected rows, or — when nothing is selected — every visible
 * (filtered) row. Ninocat's own connections are flagged (`mine`) and hidden by
 * default. A transaction is ended by killing its connection (rolls back).
 *
 * Props: connectionId, connectionName
 */
import { createSignal, createResource, createMemo, createEffect, onCleanup, For, Show } from 'solid-js';
import { formatDuration } from '../lib/format.js';
import { listProcesses, killProcesses, listTransactions } from '../lib/monitor.js';
import { toast } from '../lib/toast.js';

const COLUMNS = [
  { key: 'id', label: 'Id', num: true },
  { key: 'user', label: 'User' },
  { key: 'host', label: 'Host' },
  { key: 'db', label: 'DB' },
  { key: 'command', label: 'Command' },
  { key: 'time', label: 'Time', num: true },
  { key: 'state', label: 'State' },
  { key: 'info', label: 'Info' },
];

export default function ProcessMonitor(props) {
  const [view, setView] = createSignal('processes'); // 'processes' | 'transactions'
  const [tick, setTick] = createSignal(0);
  const refresh = () => setTick((t) => t + 1);

  // Each resource only fetches while its view is active (source returns null
  // otherwise → fetcher not called); both refresh off the shared `tick`.
  const [processes] = createResource(
    () => (view() === 'processes' ? [props.connectionId, tick()] : null),
    ([id]) => listProcesses(id).catch((e) => { toast.error('Load processes: ' + e.message); return []; }),
  );
  const [transactions] = createResource(
    () => (view() === 'transactions' ? [props.connectionId, tick()] : null),
    ([id]) => listTransactions(id).catch((e) => { toast.error('Load transactions: ' + e.message); return []; }),
  );

  const [autoRefresh, setAutoRefresh] = createSignal(false);
  const [intervalSec, setIntervalSec] = createSignal(5);
  const [filter, setFilter] = createSignal('');
  const [excludeNinocat, setExcludeNinocat] = createSignal(true);
  const [sortCol, setSortCol] = createSignal('time');
  const [sortDir, setSortDir] = createSignal('desc');
  const [selected, setSelected] = createSignal(new Set());
  const [busy, setBusy] = createSignal(false);

  // Selection ids live in whichever list is showing — clear on view switch.
  createEffect(() => { view(); setSelected(new Set()); });

  // Auto-refresh: fresh interval whenever the toggle/interval changes.
  createEffect(() => {
    if (!autoRefresh()) return;
    const ms = Math.max(1, Number(intervalSec()) || 1) * 1000;
    const h = setInterval(() => refresh(), ms);
    onCleanup(() => clearInterval(h));
  });

  const loading = () => (view() === 'processes' ? processes.loading : transactions.loading);

  const matchesFilter = (fields, f) => fields.some((v) => String(v ?? '').toLowerCase().includes(f));

  const visibleProcs = createMemo(() => {
    let list = processes() ?? [];
    if (excludeNinocat()) list = list.filter((p) => !p.mine);
    const f = filter().trim().toLowerCase();
    if (f) list = list.filter((p) => matchesFilter([p.id, p.user, p.host, p.db, p.command, p.state, p.info, p.time], f));
    const col = sortCol();
    const dir = sortDir() === 'asc' ? 1 : -1;
    const numeric = COLUMNS.find((c) => c.key === col)?.num;
    return [...list].sort((a, b) => {
      const av = a[col], bv = b[col];
      if (numeric) return (Number(av) - Number(bv)) * dir;
      return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
    });
  });

  const visibleTxns = createMemo(() => {
    let list = transactions() ?? [];
    if (excludeNinocat()) list = list.filter((t) => !t.mine);
    const f = filter().trim().toLowerCase();
    if (f) list = list.filter((t) => matchesFilter([t.threadId, t.user, t.host, t.db, t.state, t.sql], f));
    // Lock-holders first: idle-in-transaction, then oldest.
    return [...list].sort((a, b) => (Number(b.idle) - Number(a.idle)) || (b.ageSecs - a.ageSecs));
  });

  const activeList = () => (view() === 'processes' ? visibleProcs() : visibleTxns());
  const rowId = (row) => (view() === 'processes' ? row.id : row.threadId);

  const toggleSort = (key) => {
    if (sortCol() === key) setSortDir(sortDir() === 'asc' ? 'desc' : 'asc');
    else { setSortCol(key); setSortDir(COLUMNS.find((c) => c.key === key)?.num ? 'desc' : 'asc'); }
  };

  const isSel = (id) => selected().has(id);
  const toggleSel = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allVisibleSelected = () => activeList().length > 0 && activeList().every((r) => selected().has(rowId(r)));
  const toggleAll = () => {
    if (allVisibleSelected()) setSelected(new Set());
    else setSelected(new Set(activeList().map(rowId)));
  };

  // Selected rows, or — when nothing is selected — every visible (filtered) row.
  const killTargets = () => {
    const sel = selected();
    const list = activeList();
    if (sel.size > 0) return list.filter((r) => sel.has(rowId(r))).map(rowId);
    return list.map(rowId);
  };

  const doKill = async (mode) => {
    const ids = killTargets();
    if (ids.length === 0) { toast.info('Nothing to kill.'); return; }
    const thing = view() === 'transactions' ? 'transaction' : 'process';
    const noun = mode === 'query' ? 'query on' : 'connection for';
    const scope = selected().size > 0 ? 'selected' : 'ALL filtered';
    if (!confirm(`Kill ${noun} ${ids.length} ${scope} ${thing}${ids.length === 1 ? '' : 's'}?`)) return;
    setBusy(true);
    try {
      const r = await killProcesses(props.connectionId, ids, mode);
      const results = r.results || {};
      const failed = Object.values(results).filter((e) => e).length;
      const ok = ids.length - failed;
      if (failed) toast.error(`Killed ${ok}, ${failed} failed`);
      else toast.success(`Killed ${ok} ${thing}${ok === 1 ? '' : 's'}`);
      setSelected(new Set());
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const ninocatCount = () => {
    const list = view() === 'processes' ? (processes() ?? []) : (transactions() ?? []);
    return list.filter((r) => r.mine).length;
  };

  return (
    <div class="monitor-panel">
      <div class="browser-breadcrumb-row">
        <div class="breadcrumb mono">
          <span class="muted">{props.connectionName}</span>
          <span class="sep">/</span>
          <span class="strong">Server monitor</span>
        </div>
        <div class="monitor-view-toggle">
          <button class={`btn btn-sm ${view() === 'processes' ? '' : 'btn-ghost'}`}
                  onClick={() => setView('processes')}>Processes</button>
          <button class={`btn btn-sm ${view() === 'transactions' ? '' : 'btn-ghost'}`}
                  onClick={() => setView('transactions')}>Transactions</button>
        </div>
      </div>

      <div class="browser-toolbar monitor-toolbar">
        <button class="btn btn-sm btn-ghost" onClick={refresh} disabled={loading()}>⟳ Refresh</button>
        <label class="monitor-check">
          <input type="checkbox" checked={autoRefresh()} onChange={(e) => setAutoRefresh(e.target.checked)} />
          Auto
        </label>
        <input class="input mono" type="number" min="1" style="width:58px;"
               value={intervalSec()} onInput={(e) => setIntervalSec(parseInt(e.target.value, 10) || 1)}
               title="Auto-refresh interval (seconds)" />
        <span class="muted" style="font-size:11px;">s</span>
        <input class="input objects-filter-input" style="max-width:240px;" placeholder="Filter…"
               value={filter()} onInput={(e) => setFilter(e.target.value)} />
        <label class="monitor-check" title={`${ninocatCount()} Ninocat connection(s)`}>
          <input type="checkbox" checked={excludeNinocat()} onChange={(e) => setExcludeNinocat(e.target.checked)} />
          Hide Ninocat
        </label>
        <div class="spacer" />
        <Show when={view() === 'processes'}>
          <button class="btn btn-sm" onClick={() => doKill('query')} disabled={busy()}
                  title="KILL QUERY — abort the running statement, keep the connection">✕ Kill query</button>
        </Show>
        <button class="btn btn-sm btn-danger" onClick={() => doKill('connection')} disabled={busy()}
                title={view() === 'transactions'
                  ? 'KILL — drop the connection, rolling back its transaction'
                  : 'KILL — drop the whole connection'}>✕ Kill connection</button>
      </div>

      <div class="monitor-table-wrap">
        {/* ── Processes ── */}
        <Show when={view() === 'processes'}>
          <table class="monitor-table">
            <thead>
              <tr>
                <th class="monitor-cb"><input type="checkbox" checked={allVisibleSelected()} onChange={toggleAll} /></th>
                <For each={COLUMNS}>
                  {(c) => (
                    <th class={c.num ? 'num' : ''} onClick={() => toggleSort(c.key)} style="cursor:pointer;">
                      {c.label}
                      <Show when={sortCol() === c.key}><span class="monitor-sort">{sortDir() === 'asc' ? ' ▲' : ' ▼'}</span></Show>
                    </th>
                  )}
                </For>
              </tr>
            </thead>
            <tbody>
              <Show when={!processes.loading} fallback={<tr><td colspan="9" class="muted" style="padding:16px;">Loading…</td></tr>}>
                <Show when={visibleProcs().length === 0}>
                  <tr><td colspan="9" class="muted" style="padding:16px;">No processes match.</td></tr>
                </Show>
                <For each={visibleProcs()}>
                  {(p) => (
                    <tr classList={{ 'monitor-selected': isSel(p.id), 'monitor-mine': p.mine }}>
                      <td class="monitor-cb"><input type="checkbox" checked={isSel(p.id)} onChange={() => toggleSel(p.id)} /></td>
                      <td class="num mono">{p.id}</td>
                      <td>{p.user}<Show when={p.mine}><span class="monitor-badge">ninocat</span></Show></td>
                      <td class="mono">{p.host}</td>
                      <td class="mono">{p.db || <span class="muted">—</span>}</td>
                      <td>{p.command}</td>
                      <td class="num mono">{p.time}</td>
                      <td>{p.state || <span class="muted">—</span>}</td>
                      <td class="mono monitor-info" title={p.info}>{p.info || <span class="muted">—</span>}</td>
                    </tr>
                  )}
                </For>
              </Show>
            </tbody>
          </table>
        </Show>

        {/* ── Transactions ── */}
        <Show when={view() === 'transactions'}>
          <table class="monitor-table">
            <thead>
              <tr>
                <th class="monitor-cb"><input type="checkbox" checked={allVisibleSelected()} onChange={toggleAll} /></th>
                <th class="num">Thread</th>
                <th>User</th>
                <th>Host</th>
                <th>DB</th>
                <th>Status</th>
                <th class="num">Age</th>
                <th class="num">Locked</th>
                <th class="num">Modified</th>
                <th>SQL</th>
              </tr>
            </thead>
            <tbody>
              <Show when={!transactions.loading} fallback={<tr><td colspan="10" class="muted" style="padding:16px;">Loading…</td></tr>}>
                <Show when={visibleTxns().length === 0}>
                  <tr><td colspan="10" class="muted" style="padding:16px;">No open transactions{excludeNinocat() ? ' (Ninocat hidden)' : ''}.</td></tr>
                </Show>
                <For each={visibleTxns()}>
                  {(t) => (
                    <tr classList={{ 'monitor-selected': isSel(t.threadId), 'monitor-mine': t.mine, 'monitor-tx-idle': t.idle }}>
                      <td class="monitor-cb"><input type="checkbox" checked={isSel(t.threadId)} onChange={() => toggleSel(t.threadId)} /></td>
                      <td class="num mono">{t.threadId}</td>
                      <td>{t.user}<Show when={t.mine}><span class="monitor-badge">ninocat</span></Show></td>
                      <td class="mono">{t.host}</td>
                      <td class="mono">{t.db || <span class="muted">—</span>}</td>
                      <td>
                        <span class={t.idle ? 'monitor-tx-status-idle' : 'monitor-tx-status-active'}>
                          {t.idle ? 'idle in tx' : 'active'}
                        </span>
                        <Show when={t.state && t.state !== 'RUNNING'}><span class="muted"> · {t.state}</span></Show>
                      </td>
                      <td class="num mono">{formatDuration(t.ageSecs)}</td>
                      <td class="num mono">{t.rowsLocked}</td>
                      <td class="num mono">{t.rowsModified}</td>
                      <td class="mono monitor-info" title={t.sql}>{t.sql || <span class="muted">—</span>}</td>
                    </tr>
                  )}
                </For>
              </Show>
            </tbody>
          </table>
        </Show>
      </div>

      <div class="monitor-footer muted mono">
        {activeList().length} shown{selected().size > 0 ? ` · ${selected().size} selected` : ''}
        <Show when={excludeNinocat() && ninocatCount() > 0}> · {ninocatCount()} Ninocat hidden</Show>
      </div>
    </div>
  );
}
